'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface EndorsementFormModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const EndorsementFormModal: React.FC<EndorsementFormModalProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    category: '',
    name: '',
    position: '',
    company: '',
    message: ''
  });

  const categories = [
    'Laboratory Operations Management',
    'Regulatory Compliance & Quality Assurance',
    'Team Leadership & Development'
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission logic here
    console.log('Endorsement submitted:', formData);
    // Reset form and close modal
    setFormData({
      category: '',
      name: '',
      position: '',
      company: '',
      message: ''
    });
    onClose();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card rounded-2xl border border-border shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-accent/10 text-accent p-2 rounded-lg">
              <Icon name="HandThumbUpIcon" size={24} />
            </div>
            <div>
              <h2 className="font-heading font-bold text-2xl text-text-primary">
                Endorse Curtis Kendal
              </h2>
              <p className="text-text-secondary text-sm">
                Share your professional validation
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary transition-colors duration-200"
          >
            <Icon name="XMarkIcon" size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Category Dropdown */}
          <div>
            <label htmlFor="category" className="block text-sm font-semibold text-text-primary mb-2">
              Category of Endorsement <span className="text-red-500">*</span>
            </label>
            <select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-muted/30 border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-200"
            >
              <option value="">Select a category</option>
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          {/* Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-semibold text-text-primary mb-2">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Your full name"
              className="w-full px-4 py-3 bg-muted/30 border border-border rounded-lg text-text-primary placeholder:text-text-secondary/50 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-200"
            />
          </div>

          {/* Position */}
          <div>
            <label htmlFor="position" className="block text-sm font-semibold text-text-primary mb-2">
              Position <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="position"
              name="position"
              value={formData.position}
              onChange={handleChange}
              required
              placeholder="Your job title"
              className="w-full px-4 py-3 bg-muted/30 border border-border rounded-lg text-text-primary placeholder:text-text-secondary/50 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-200"
            />
          </div>

          {/* Company */}
          <div>
            <label htmlFor="company" className="block text-sm font-semibold text-text-primary mb-2">
              Company <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              required
              placeholder="Your company name"
              className="w-full px-4 py-3 bg-muted/30 border border-border rounded-lg text-text-primary placeholder:text-text-secondary/50 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-200"
            />
          </div>

          {/* Endorsement Message */}
          <div>
            <label htmlFor="message" className="block text-sm font-semibold text-text-primary mb-2">
              Endorsement Message <span className="text-red-500">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              rows={5}
              placeholder="Share your professional validation and experience working with Curtis..."
              className="w-full px-4 py-3 bg-muted/30 border border-border rounded-lg text-text-primary placeholder:text-text-secondary/50 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-200 resize-none"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 rounded-lg border border-border text-text-primary hover:bg-muted/30 transition-colors duration-200 font-semibold"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-3 rounded-lg bg-accent text-white hover:bg-accent/90 transition-colors duration-200 font-semibold flex items-center space-x-2"
            >
              <Icon name="PaperAirplaneIcon" size={20} />
              <span>Submit Endorsement</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EndorsementFormModal;